import React, { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useFormik } from "formik";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { Button } from "../common/button";
import { Client } from "../../types/Client";
import { EmailRecipientsField } from "./EmailRecipientsField";
import { ClientNamesField } from "./ClientNamesField";

interface ClientDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (customerNameOrId: string, client: Partial<Client>) => void;
  customerId: string | null;
  client: Client | null;
}

const validationSchema = yup.object({
  customerName: yup.string().required("Customer name is required"),
  email: yup
    .string()
    .email("Must be a valid email")
    .required("Email is required"),
  email_password: yup
    .string()
    .min(8, "Password must be at least 8 characters")
    .required("Email password is required"),
  base_url: yup
    .string()
    .url("Must be a valid URL")
    .required("Base URL is required"),
  auth_type: yup
    .string()
    .oneOf(["priority_token", "username_password"], "Invalid auth type")
    .required("Auth type is required"),
  priority_token: yup.string().when("auth_type", {
    is: "priority_token",
    then: (schema) => schema.required("Priority token is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  username: yup.string().when("auth_type", {
    is: "username_password",
    then: (schema) => schema.required("Username is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  password: yup.string().when("auth_type", {
    is: "username_password",
    then: (schema) => schema.required("Password is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  monthly_quota: yup
    .number()
    .positive("Must be a positive number")
    .integer("Must be an integer"),
});

const ClientDialog: React.FC<ClientDialogProps> = ({
  open,
  onClose,
  onSave,
  customerId,
  client,
}) => {
  const { t } = useTranslation();
  const isEditing = !!customerId;

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema,
    initialValues: {
      customerName: client?.customer_name || "",
      email: client?.email || "",
      email_password: client?.email_password || "",
      base_url: client?.ERP.base_url || "",
      clients_names: client?.ERP.clients_names || [],
      auth_type: (client?.ERP.auth_type || "priority_token") as "priority_token" | "username_password",
      priority_token: client?.ERP.priority_token || "",
      username: client?.ERP.username || "",
      password: client?.ERP.password || "",
      success_mails: client?.configuration?.success_mails || [],
      failure_mails: client?.configuration?.failure_mails || [],
      daily_digest_recipients:
        client?.configuration?.daily_digest_recipients || [],
      quota_warning: client?.configuration?.quota_warning || [],
      monthly_quota: client?.subscription?.monthly_quota || 300,
      auto_renew: client?.subscription?.auto_renew ?? true,
    },
    onSubmit: (values) => {
      const clientData: Partial<Client> = {
        customer_name: values.customerName,
        email: values.email,
        email_password: values.email_password,
        ERP: {
          enabled: true,
          base_url: values.base_url,
          clients_names: values.clients_names,
          auth_type: values.auth_type,
          ...(values.auth_type === "priority_token"
            ? { priority_token: values.priority_token }
            : { username: values.username, password: values.password }
          ),
        },
        configuration: {
          success_mails: values.success_mails,
          failure_mails: values.failure_mails,
          daily_digest_recipients: values.daily_digest_recipients,
          quota_warning: values.quota_warning,
        },
      };

      // Add optional fields for new customers
      if (!isEditing) {
        (clientData as any).token_name = values.customerName;
        (clientData as any).folder_name = values.customerName;
        (clientData as any).monthly_quota = values.monthly_quota;
        (clientData as any).auto_renew = values.auto_renew;
      }

      // Use customerId for updates, customerName for new customers
      onSave(isEditing ? customerId! : values.customerName, clientData);
    },
  });

  useEffect(() => {
    if (!open) {
      formik.resetForm();
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditing ? `${t("EDIT_CUSTOMER")}: ${client?.customer_name}` : t("ADD_CUSTOMER")}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <TextField
            fullWidth
            label={t("CUSTOMER_NAME")}
            name="customerName"
            value={formik.values.customerName}
            onChange={formik.handleChange}
            error={formik.touched.customerName && Boolean(formik.errors.customerName)}
            helperText={
              (formik.touched.customerName && formik.errors.customerName) ||
              "Enter a unique customer name (e.g., yael_demo)"
            }
            disabled={isEditing}
          />

          <TextField
            fullWidth
            label={t("EMAIL") || "Client Email"}
            name="email"
            type="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={
              (formik.touched.email && formik.errors.email) ||
              "Enter a valid email address (e.g., user@example.com)"
            }
            required
          />

          <TextField
            fullWidth
            label={t("EMAIL_PASSWORD") || "Email Password"}
            name="email_password"
            type="password"
            value={formik.values.email_password}
            onChange={formik.handleChange}
            error={
              formik.touched.email_password &&
              Boolean(formik.errors.email_password)
            }
            helperText={
              (formik.touched.email_password && formik.errors.email_password) ||
              (isEditing
                ? "Leave blank to keep current password"
                : "Minimum 8 characters required")
            }
            required={!isEditing}
          />

          <Typography variant="h6" sx={{ mt: 2 }}>
            {t("ERP_CONFIGURATION")}
          </Typography>

          <TextField
            fullWidth
            label={t("BASE_URL")}
            name="base_url"
            value={formik.values.base_url}
            onChange={formik.handleChange}
            error={formik.touched.base_url && Boolean(formik.errors.base_url)}
            helperText={
              (formik.touched.base_url && formik.errors.base_url) ||
              "Enter a valid URL (e.g., https://api.example.com)"
            }
          />

          <ClientNamesField
            label={t("CLIENT_NAMES") || "Client Names"}
            clientNames={formik.values.clients_names}
            onChange={(clientNames) =>
              formik.setFieldValue("clients_names", clientNames)
            }
          />

          <FormControl component="fieldset" sx={{ mt: 2 }}>
            <FormLabel component="legend">
              {t("AUTH_METHOD") || "Authentication Method"}
            </FormLabel>
            <RadioGroup
              row
              name="auth_type"
              value={formik.values.auth_type}
              onChange={formik.handleChange}
            >
              <FormControlLabel
                value="priority_token"
                control={<Radio />}
                label={t("PRIORITY_TOKEN") || "Priority Token"}
              />
              <FormControlLabel
                value="username_password"
                control={<Radio />}
                label={t("USERNAME_PASSWORD") || "Username & Password"}
              />
            </RadioGroup>
          </FormControl>

          {formik.values.auth_type === "priority_token" && (
            <TextField
              fullWidth
              label={t("PRIORITY_TOKEN")}
              name="priority_token"
              value={formik.values.priority_token}
              onChange={formik.handleChange}
              error={
                formik.touched.priority_token && Boolean(formik.errors.priority_token)
              }
              helperText={
                (formik.touched.priority_token && formik.errors.priority_token) ||
                "Enter the priority token for the API"
              }
            />
          )}

          {formik.values.auth_type === "username_password" && (
            <>
              <TextField
                fullWidth
                label={t("USERNAME") || "Username"}
                name="username"
                value={formik.values.username}
                onChange={formik.handleChange}
                error={
                  formik.touched.username && Boolean(formik.errors.username)
                }
                helperText={
                  (formik.touched.username && formik.errors.username) ||
                  "Enter the API username"
                }
              />
              <TextField
                fullWidth
                label={t("PASSWORD") || "Password"}
                name="password"
                type="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                helperText={
                  (formik.touched.password && formik.errors.password) ||
                  "Enter the API password (will be encoded as Base64)"
                }
              />
            </>
          )}

          <Accordion
            sx={{
              mt: 3,
              border: "1px solid #e3f2fd",
              borderRadius: "8px !important",
              boxShadow: "0 2px 4px rgba(33, 150, 243, 0.1)",
              "&:before": { display: "none" },
              backgroundColor: "#f8fcff",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: "#e3f2fd",
                borderRadius: "8px",
                "&.Mui-expanded": {
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                },
              }}
            >
              <Typography sx={{ fontWeight: 600, color: "#1976d2" }}>
                {t("EMAIL_CONFIGURATION") || "Email Configuration"}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ backgroundColor: "#f8fcff", pt: 3 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <EmailRecipientsField
                  label={t("SUCCESS_EMAILS") || "Success Email Recipients"}
                  secondaryLabels={t("DISPLAY_NAME") || "Display Name"}
                  recipients={formik.values.success_mails}
                  onChange={(recipients) =>
                    formik.setFieldValue("success_mails", recipients)
                  }
                />
                <EmailRecipientsField
                  label={t("FAILURE_EMAILS") || "Failure Email Recipients"}
                  secondaryLabels={t("DISPLAY_NAME") || "Display Name"}
                  recipients={formik.values.failure_mails}
                  onChange={(recipients) =>
                    formik.setFieldValue("failure_mails", recipients)
                  }
                />
                <EmailRecipientsField
                  label={t("DAILY_DIGEST_EMAILS") || "Daily Digest Recipients"}
                  secondaryLabels={t("DISPLAY_NAME") || "Display Name"}
                  recipients={formik.values.daily_digest_recipients}
                  onChange={(recipients) =>
                    formik.setFieldValue("daily_digest_recipients", recipients)
                  }
                />
                <EmailRecipientsField
                  label={t("QUOTA_WARNING_EMAILS") || "Quota Warning Recipients"}
                  secondaryLabels={t("DISPLAY_NAME") || "Display Name"}
                  recipients={formik.values.quota_warning}
                  onChange={(recipients) =>
                    formik.setFieldValue("quota_warning", recipients)
                  }
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion
            sx={{
              mt: 2,
              border: "1px solid #e3f2fd",
              borderRadius: "8px !important",
              boxShadow: "0 2px 4px rgba(33, 150, 243, 0.1)",
              "&:before": { display: "none" },
              backgroundColor: "#f8fcff",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: "#e3f2fd",
                borderRadius: "8px",
                "&.Mui-expanded": {
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                },
              }}
            >
              <Typography sx={{ fontWeight: 600, color: "#1976d2" }}>
                {t("OPTIONAL_SETTINGS") || "Optional Settings"}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ backgroundColor: "#f8fcff", pt: 3 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  fullWidth
                  label={t("MONTHLY_QUOTA") || "Monthly Quota"}
                  name="monthly_quota"
                  type="number"
                  value={formik.values.monthly_quota}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.monthly_quota &&
                    Boolean(formik.errors.monthly_quota)
                  }
                  helperText={
                    (formik.touched.monthly_quota &&
                      formik.errors.monthly_quota) ||
                    "Enter a positive integer (Default: 300)"
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formik.values.auto_renew}
                      onChange={(e) =>
                        formik.setFieldValue("auto_renew", e.target.checked)
                      }
                    />
                  }
                  label={t("AUTO_RENEW") || "Auto-renew subscription"}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button label={t("CANCEL")} onClick={onClose} />
        <Button
          label={isEditing ? t("UPDATE") : t("ADD")}
          onClick={formik.submitForm}
          disabled={!formik.isValid || !formik.dirty}
        />
      </DialogActions>
    </Dialog>
  );
};

export default ClientDialog;
